import React, { useState, useEffect } from 'react';
import { DesignSystemService, type DesignSystemConfig as DesignSystemConfigType } from '../../services/designSystemService';
import { logger } from '../../services/loggingService';

interface DesignSystemConfigProps {
  projectId: string;
  blueprintId?: string;
  onComplete: (designSystemId: string) => void;
}

export function DesignSystemConfig({ projectId, blueprintId, onComplete }: DesignSystemConfigProps) {
  const [config, setConfig] = useState<DesignSystemConfigType>({
    projectId,
    userId: '',
    name: '',
    primaryColor: '#3B82F6',
    secondaryColor: '#10B981',
    accentColor: '#F59E0B',
    neutralColors: {
      '50': '#F9FAFB',
      '100': '#F3F4F6',
      '200': '#E5E7EB',
      '300': '#D1D5DB',
      '400': '#9CA3AF',
      '500': '#6B7280',
      '600': '#4B5563',
      '700': '#374151',
      '800': '#1F2937',
      '900': '#111827'
    },
    semanticColors: {
      success: '#10B981',
      warning: '#F59E0B',
      error: '#EF4444',
      info: '#3B82F6'
    },
    fontFamilyPrimary: 'Inter, sans-serif',
    fontFamilySecondary: 'system-ui',
    fontScale: {
      xs: '0.75rem',
      sm: '0.875rem',
      base: '1rem',
      lg: '1.125rem',
      xl: '1.25rem',
      '2xl': '1.5rem',
      '3xl': '1.875rem',
      '4xl': '2.25rem'
    },
    spacingScale: {
      '0': '0',
      '1': '0.25rem',
      '2': '0.5rem',
      '3': '0.75rem',
      '4': '1rem',
      '6': '1.5rem',
      '8': '2rem',
      '12': '3rem',
      '16': '4rem'
    },
    borderRadius: {
      sm: '0.125rem',
      md: '0.375rem',
      lg: '0.5rem',
      xl: '0.75rem',
      '2xl': '1rem',
      full: '9999px'
    },
    componentVariants: {},
    brandAssets: []
  });

  const [loading, setLoading] = useState(false);
  const [activeTab, setActiveTab] = useState<'colors' | 'typography' | 'spacing' | 'assets'>('colors');

  useEffect(() => {
    if (blueprintId) {
      loadFromBlueprint();
    }
  }, [blueprintId]);

  const loadFromBlueprint = async () => {
    if (!blueprintId) return;
    
    try {
      setLoading(true);
      const generatedConfig = await DesignSystemService.generateFromBlueprint(blueprintId);
      setConfig(generatedConfig);
      logger.info('Design system loaded from blueprint', { blueprintId });
    } catch (error) {
      logger.error('Failed to load design system from blueprint', { error, blueprintId });
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    try {
      setLoading(true);
      const designSystemId = await DesignSystemService.createDesignSystem(projectId, config);
      logger.info('Design system saved', { designSystemId, projectId });
      onComplete(designSystemId);
    } catch (error) {
      logger.error('Failed to save design system', { error, projectId });
    } finally {
      setLoading(false);
    }
  };

  const handleColorChange = (colorType: string, value: string) => {
    setConfig(prev => ({
      ...prev,
      [colorType]: value
    }));
  };

  const handleNeutralColorChange = (shade: string, value: string) => {
    setConfig(prev => ({
      ...prev,
      neutralColors: {
        ...prev.neutralColors,
        [shade]: value
      }
    }));
  };

  const handleSemanticColorChange = (semantic: string, value: string) => {
    setConfig(prev => ({
      ...prev,
      semanticColors: {
        ...prev.semanticColors,
        [semantic]: value
      }
    }));
  };

  const handleFontChange = (fontType: string, value: string) => {
    setConfig(prev => ({
      ...prev,
      [fontType]: value
    }));
  };

  const handleScaleChange = (scaleType: string, key: string, value: string) => {
    setConfig(prev => ({
      ...prev,
      [scaleType]: {
        ...prev[scaleType as keyof typeof prev] as Record<string, string>,
        [key]: value
      }
    }));
  };

  const handleAssetUpload = async (file: File, assetType: 'logo' | 'favicon' | 'image' | 'icon') => {
    try {
      setLoading(true);
      const url = await DesignSystemService.uploadBrandAsset(file, config.id || '', assetType);
      
      setConfig(prev => ({
        ...prev,
        brandAssets: [...prev.brandAssets, {
          name: file.name,
          url,
          type: assetType
        }]
      }));
      
      logger.info('Asset uploaded', { assetType, url });
    } catch (error) {
      logger.error('Failed to upload asset', { error, assetType });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="max-w-4xl mx-auto p-6">
      <div className="mb-8">
        <h2 className="text-2xl font-bold text-gray-900 mb-2">Design System Configuration</h2>
        <p className="text-gray-600">
          Configure your design system colors, typography, spacing, and brand assets.
        </p>
      </div>

      {/* Tab Navigation */}
      <div className="border-b border-gray-200 mb-6">
        <nav className="-mb-px flex space-x-8">
          {[
            { id: 'colors', label: 'Colors', icon: '🎨' },
            { id: 'typography', label: 'Typography', icon: '📝' },
            { id: 'spacing', label: 'Spacing', icon: '📏' },
            { id: 'assets', label: 'Assets', icon: '🖼️' }
          ].map((tab) => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id as any)}
              className={`py-2 px-1 border-b-2 font-medium text-sm ${
                activeTab === tab.id
                  ? 'border-accent-500 text-accent-500'
                  : 'border-transparent text-secondary hover:text-primary hover:border-default'
              }`}
            >
              <span className="mr-2">{tab.icon}</span>
              {tab.label}
            </button>
          ))}
        </nav>
      </div>

      {/* Tab Content */}
      <div className="space-y-6">
        {activeTab === 'colors' && (
          <div className="space-y-6">
            <div>
              <h3 className="text-lg font-semibold text-gray-900 mb-4">Primary Colors</h3>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Primary Color
                  </label>
                  <div className="flex items-center space-x-2">
                    <input
                      type="color"
                      value={config.primaryColor}
                      onChange={(e) => handleColorChange('primaryColor', e.target.value)}
                      className="w-12 h-10 border border-gray-300 rounded cursor-pointer"
                    />
                    <input
                      type="text"
                      value={config.primaryColor}
                      onChange={(e) => handleColorChange('primaryColor', e.target.value)}
                      className="flex-1 px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary"
                    />
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Secondary Color
                  </label>
                  <div className="flex items-center space-x-2">
                    <input
                      type="color"
                      value={config.secondaryColor}
                      onChange={(e) => handleColorChange('secondaryColor', e.target.value)}
                      className="w-12 h-10 border border-gray-300 rounded cursor-pointer"
                    />
                    <input
                      type="text"
                      value={config.secondaryColor}
                      onChange={(e) => handleColorChange('secondaryColor', e.target.value)}
                      className="flex-1 px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary"
                    />
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Accent Color
                  </label>
                  <div className="flex items-center space-x-2">
                    <input
                      type="color"
                      value={config.accentColor}
                      onChange={(e) => handleColorChange('accentColor', e.target.value)}
                      className="w-12 h-10 border border-gray-300 rounded cursor-pointer"
                    />
                    <input
                      type="text"
                      value={config.accentColor}
                      onChange={(e) => handleColorChange('accentColor', e.target.value)}
                      className="flex-1 px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary"
                    />
                  </div>
                </div>
              </div>
            </div>

            <div>
              <h3 className="text-lg font-semibold text-gray-900 mb-4">Neutral Colors</h3>
              <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
                {Object.entries(config.neutralColors).map(([shade, color]) => (
                  <div key={shade}>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      {shade}
                    </label>
                    <div className="flex items-center space-x-2">
                      <input
                        type="color"
                        value={color}
                        onChange={(e) => handleNeutralColorChange(shade, e.target.value)}
                        className="w-12 h-10 border border-gray-300 rounded cursor-pointer"
                      />
                      <input
                        type="text"
                        value={color}
                        onChange={(e) => handleNeutralColorChange(shade, e.target.value)}
                        className="flex-1 px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary"
                      />
                    </div>
                  </div>
                ))}
              </div>
            </div>

            <div>
              <h3 className="text-lg font-semibold text-gray-900 mb-4">Semantic Colors</h3>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                {Object.entries(config.semanticColors).map(([semantic, color]) => (
                  <div key={semantic}>
                    <label className="block text-sm font-medium text-gray-700 mb-2 capitalize">
                      {semantic}
                    </label>
                    <div className="flex items-center space-x-2">
                      <input
                        type="color"
                        value={color}
                        onChange={(e) => handleSemanticColorChange(semantic, e.target.value)}
                        className="w-12 h-10 border border-gray-300 rounded cursor-pointer"
                      />
                      <input
                        type="text"
                        value={color}
                        onChange={(e) => handleSemanticColorChange(semantic, e.target.value)}
                        className="flex-1 px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary"
                      />
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}

        {activeTab === 'typography' && (
          <div className="space-y-6">
            <div>
              <h3 className="text-lg font-semibold text-gray-900 mb-4">Font Families</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Primary Font
                  </label>
                  <input
                    type="text"
                    value={config.fontFamilyPrimary}
                    onChange={(e) => handleFontChange('fontFamilyPrimary', e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary"
                    placeholder="Inter, sans-serif"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Secondary Font
                  </label>
                  <input
                    type="text"
                    value={config.fontFamilySecondary}
                    onChange={(e) => handleFontChange('fontFamilySecondary', e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary"
                    placeholder="system-ui"
                  />
                </div>
              </div>
            </div>

            <div>
              <h3 className="text-lg font-semibold text-gray-900 mb-4">Font Scale</h3>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                {Object.entries(config.fontScale).map(([size, value]) => (
                  <div key={size}>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      {size}
                    </label>
                    <input
                      type="text"
                      value={value}
                      onChange={(e) => handleScaleChange('fontScale', size, e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary"
                    />
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}

        {activeTab === 'spacing' && (
          <div className="space-y-6">
            <div>
              <h3 className="text-lg font-semibold text-gray-900 mb-4">Spacing Scale</h3>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                {Object.entries(config.spacingScale).map(([size, value]) => (
                  <div key={size}>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      {size}
                    </label>
                    <input
                      type="text"
                      value={value}
                      onChange={(e) => handleScaleChange('spacingScale', size, e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary"
                    />
                  </div>
                ))}
              </div>
            </div>

            <div>
              <h3 className="text-lg font-semibold text-gray-900 mb-4">Border Radius</h3>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                {Object.entries(config.borderRadius).map(([size, value]) => (
                  <div key={size}>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      {size}
                    </label>
                    <input
                      type="text"
                      value={value}
                      onChange={(e) => handleScaleChange('borderRadius', size, e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary"
                    />
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}

        {activeTab === 'assets' && (
          <div className="space-y-6">
            <div>
              <h3 className="text-lg font-semibold text-gray-900 mb-4">Brand Assets</h3>
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Logo Upload
                  </label>
                  <input
                    type="file"
                    accept="image/png,image/svg+xml,image/jpeg"
                    onChange={(e) => {
                      const file = e.target.files?.[0];
                      if (file) handleAssetUpload(file, 'logo');
                    }}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Favicon Upload
                  </label>
                  <input
                    type="file"
                    accept="image/x-icon,image/png"
                    onChange={(e) => {
                      const file = e.target.files?.[0];
                      if (file) handleAssetUpload(file, 'favicon');
                    }}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Additional Images
                  </label>
                  <input
                    type="file"
                    accept="image/png,image/jpeg,image/webp"
                    multiple
                    onChange={(e) => {
                      const files = Array.from(e.target.files || []);
                      files.forEach(file => handleAssetUpload(file, 'image'));
                    }}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary"
                  />
                </div>
              </div>
            </div>

            {config.brandAssets.length > 0 && (
              <div>
                <h3 className="text-lg font-semibold text-gray-900 mb-4">Uploaded Assets</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                  {config.brandAssets.map((asset, index) => (
                    <div key={index} className="border border-gray-200 rounded-lg p-4">
                      <img
                        src={asset.url}
                        alt={asset.name}
                        className="w-full h-20 object-contain mb-2"
                      />
                      <p className="text-sm font-medium text-gray-900">{asset.name}</p>
                      <p className="text-xs text-gray-500 capitalize">{asset.type}</p>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Actions */}
      <div className="mt-8 flex justify-end space-x-4">
        <button
          onClick={handleSave}
          disabled={loading}
          className="btn btn-primary px-6 py-2 rounded-md focus:outline-none focus:ring-primary disabled:opacity-50"
        >
          {loading ? 'Saving...' : 'Save Design System'}
        </button>
      </div>
    </div>
  );
}
